/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/SaveSection/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import styles from "./SaveSection.module.css";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { ReactComponent as SaveIcon } from "../../assets/icons/CompanyEditPage/SaveIcon.svg";

const SaveSection = ({ onSave, hasChanges, loading, hasValidationErrors }) => {
  const { t } = useTranslation();

  const isButtonDisabled = !hasChanges || loading || hasValidationErrors;

  return (
    <div className={styles.saveSectionContainer}>
      <div className={styles.saveSectionContent}>
        <div className={styles.messageContainer}>
          {hasValidationErrors ? (
            <div className={styles.errorMessage}>
              {t("components.saveSection.validationErrorsMessage")}
            </div>
          ) : (
            <div className={styles.message}>
              {t("components.saveSection.saveChangesMessage")}
            </div>
          )}
        </div>
        <button
          className={`${styles.saveButton} ${
            hasValidationErrors ? styles.saveButtonDisabled : ""
          }`}
          onClick={onSave}
          disabled={isButtonDisabled}
          title={
            hasValidationErrors
              ? t("components.saveSection.validationErrorsTooltip")
              : ""
          }
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <div className={styles.saveButtonContent}>
              <SaveIcon className={styles.saveIcon} />
              {t("components.saveSection.saveChangesButton")}
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default SaveSection;
